import { Form } from "antd"
import {
  Button,
  Card,
  Divider,
  Flex,
  Group,
  Icon,
  InputNumber,
  Legend,
  Modal,
  SelectInput,
  Skeleton,
  Text,
  TextInput,
  Tooltip,
  notification,
} from "components/common"
import MonthPicker from "components/common/monthPicker/monthPicker"
import { useGetClientDetailQuery } from "features/clients/detail/api"
import ClientProfileForm from "features/tasks/components/clientProfileForm/clientProfileForm"
import { useGetCountriesQuery, useLazyGetProjectsQuery } from "features/shared/api"
import { useAddWealthPipelineLeadMutation, useLazyGetWealthPipelineStagesQuery } from "features/salesPipeline/api"
import { schemaRules } from "features/shared/utils"
import AddReviewClientStaffHeader from "features/leads/components/addReviewLead/components/addReviewClientStaffHeader"
import { forwardRef, useCallback, useMemo, useRef, useState } from "react"
import theme from "utils/themeVars"
import { t } from "i18next"
import getInvestmentInitialValues from "./addInvestmentInitialValues"
import getInvestmentValidationSchema from "./addInvestmentValidationSchema"
import { buildInvestmentProfilingRows, GetProfilineValue } from "./utilis"

const AddInvestmentWealthPipelineModal = forwardRef(
  ({ lead, client, onSubmitted = () => {}, isEdit = false, onDismiss }, ref) => {
    const [form] = Form.useForm()
    const clientProfileRef = useRef()
    const stageId = Form.useWatch("pipelineStageId", form)
    const [isClientProfileVisible, setIsClientProfileVisible] = useState(false)

    const rules = schemaRules(getInvestmentValidationSchema())
    const [addWealthPipelineLead, { isLoading: isSubmitting }] = useAddWealthPipelineLeadMutation()

    const clientId = lead?.Client?.id ?? lead?.Lead?.Client?.id

    const {
      data: clientDetail,
      isFetching: isClientDetailLoading,
      refetch: refetchClientDetail,
    } = useGetClientDetailQuery(clientId, { skip: !clientId })

    const clientDetailMatchesClient = useMemo(
      () => Boolean(clientId && clientDetail && String(clientDetail.id) === String(clientId)),
      [clientDetail, clientId]
    )
    const clientPayload = clientDetailMatchesClient ? clientDetail : undefined
    const isClientPanelLoading = Boolean(clientId && !clientDetailMatchesClient)

    const [getProjects, { data: projects = [], isFetching: isFetchingProjects, isLoading: loadingProjects }] =
      useLazyGetProjectsQuery()
    const [getWealthStages, { data: wealthStages, isFetching: isStagesLoading }] = useLazyGetWealthPipelineStagesQuery()
    const { data: countries = [] } = useGetCountriesQuery()

    const stages = wealthStages?.stages ?? []
    const subStages = wealthStages?.childrenByStageId ?? {}

    const showFieldSkeletons = loadingProjects || isStagesLoading

    const resetInvestmentModalClientState = useCallback(() => {
      setIsClientProfileVisible(false)
      clientProfileRef?.current?.setVisible(false)
    }, [])

    console.log("+++++++++++++lead", lead)

    const onModalVisible = useCallback(async () => {
      if (!lead) return
      form.resetFields()
      form.setFieldsValue(getInvestmentInitialValues({ lead, client, isEdit }))
      const [stagesResult, projectsResult] = await Promise.allSettled([
        getWealthStages(undefined, true).unwrap(),
        getProjects(
          {
            skipPpaExpired: true,
            withSaleableListings: true,
            status: "on",
            financials: true,
            paymentInfo: true,
          },
          true
        ).unwrap(),
      ])
      const failed = [stagesResult, projectsResult].find(r => r.status === "rejected")
      if (failed?.reason) {
        const e = failed.reason
        notification.error({ message: e?.error || e?.message || e?.error?.message })
      }
      form.setFieldsValue(getInvestmentInitialValues({ lead, client, isEdit }))
      if (clientId && !isEdit) {
        try {
          const { data } = await refetchClientDetail()
          if (data && !data.hasFilledWmProfilingFields) {
            setIsClientProfileVisible(true)
            clientProfileRef?.current?.setVisible(true)
          }
        } catch {
          /* non-blocking */
        }
      }
    }, [client, clientId, form, getProjects, isEdit, lead])

    const onFinish = useCallback(
      async values => {
        try {
          const inquiryLeadId = lead?.inquiryId ?? lead?.leadId ?? lead?.id
          await addWealthPipelineLead({
            payload: values,
            leadId: !isEdit ? inquiryLeadId : undefined,
            id: isEdit ? lead?.id : undefined,
          }).unwrap()
          ref?.current?.setVisible(false)
          form.resetFields()
          resetInvestmentModalClientState()
          notification.success({
            message: isEdit ? t("Lead Updated In Wealth Pipeline") : t("Lead Added To Wealth Pipeline"),
          })
          onSubmitted()
        } catch (error) {
          notification.error({ message: error?.data?.message || error?.message || t("Something went wrong") })
        }
      },
      [
        addWealthPipelineLead,
        form,
        isEdit,
        lead?.id,
        lead?.inquiryId,
        lead?.leadId,
        onSubmitted,
        ref,
        resetInvestmentModalClientState,
        t,
      ]
    )

    const inputSkeleton = () => <Skeleton />

    const investmentDetailsLabel = t("Investment Details")

    const investmentProfilingRows = useMemo(
      () => buildInvestmentProfilingRows(clientDetail?.profiling, countries),
      [clientDetail?.profiling, countries]
    )

    const ClientProfileDetailCard = () => (
      <Card
        Shadow={false}
        bodyStyle={{ padding: 16, paddingTop: 12 }}
        style={{ marginTop: 12, backgroundColor: theme["primary-light"], borderRadius: 8, border: "none" }}
      >
        <Group template="repeat(1, 1fr)" gap="12px 24px">
          {investmentProfilingRows.map(row => (
            <div key={row.key}>
              <Text type="secondaryLight" className="fs12" block>
                {row.label}
              </Text>
              <div className="mti-4">
                {!row.isEmpty ? (
                  <GetProfilineValue row={row} loading={isClientPanelLoading || isClientDetailLoading} />
                ) : (
                  "-"
                )}
              </div>
            </div>
          ))}
        </Group>
      </Card>
    )

    return (
      <Modal
        ref={ref}
        width={1000}
        title={isEdit ? t("Update Lead In Pipeline") : t("Add Lead In Pipeline")}
        destroyOnClose
        onModalVisible={onModalVisible}
        afterOpenChange={open => {
          if (!open) onDismiss?.()
        }}
        onOk={() => form.submit()}
        okText={isEdit ? t("Update") : t("Add")}
        onCancel={() => {
          ref?.current?.setVisible(false)
          form.resetFields()
          resetInvestmentModalClientState()
        }}
        cancelText={t("Cancel")}
        loading={isSubmitting || isClientPanelLoading || isFetchingProjects || loadingProjects || isStagesLoading}
      >
        <Form form={form} layout="vertical" preserve={false} onFinish={onFinish}>
          <Group template="minmax(300px, 0.95fr) minmax(0, 1.2fr)" gap="24px" style={{ alignItems: "flex-start" }}>
            <Card Shadow={false} bodyStyle={{ padding: 20 }} style={{ position: "sticky", top: 0 }}>
              <Flex justify="space-between" align="center" wrap="wrap" style={{ gap: 8 }}>
                <Text className="bold fs16 gray-900">{t("Client Information")}</Text>
                <Button
                  type="link"
                  className="text-link"
                  text={t("Edit")}
                  onClick={() => clientProfileRef?.current?.setVisible(true)}
                />
              </Flex>
              <Divider style={{ marginTop: 12, marginBottom: 16 }} />

              <AddReviewClientStaffHeader
                client={clientPayload}
                isLoading={isClientPanelLoading || isClientDetailLoading}
                showClientType={true}
              />
              <Legend title={investmentDetailsLabel} style={{ marginTop: 12 }} />
              <ClientProfileDetailCard />
            </Card>

            <div style={{ minWidth: 0 }}>
              <Legend title={t("Interested Project")} />
              <Form.Item name="interestedProjectId" label={`${t("Project")}`} rules={rules} required>
                <SelectInput
                  placeholder={t("Select Project")}
                  options={projects}
                  skeletonLoading={loadingProjects}
                  inputLoading={isFetchingProjects}
                />
              </Form.Item>

              <Text className="bold fs16 mbe-12 mti-8" block>
                {t("Deal Information")}
              </Text>
              <Divider className="m-0 mbe-16" />

              <Group template="repeat(2, minmax(0, 1fr))" gap="12px">
                <Form.Item name="dealValue" label={`${t("Deal Value")}`} rules={rules} required>
                  {showFieldSkeletons ? inputSkeleton() : <InputNumber min={0} style={{ width: "100%" }} />}
                </Form.Item>
                <Form.Item name="expectedRevenue" label={`${t("Expected Revenue")}`} rules={rules} required>
                  {showFieldSkeletons ? inputSkeleton() : <InputNumber min={0} style={{ width: "100%" }} />}
                </Form.Item>
              </Group>

              <Text className="bold fs16 mbe-12 mti-8" block>
                {t("Conversion")}
              </Text>
              <Divider className="m-0 mbe-16" />

              <Group template="repeat(2, minmax(0, 1fr))" gap="12px">
                <Form.Item name="pipelineStageId" label={`${t("Pipeline Stage")}`} rules={rules} required>
                  {showFieldSkeletons ? (
                    inputSkeleton()
                  ) : (
                    <SelectInput
                      placeholder={t("Select Stage")}
                      options={stages}
                      skeletonLoading={isStagesLoading}
                      inputLoading={isStagesLoading}
                      onChange={value => form.setFieldsValue({ pipelineStageId: value, pipelineSubStageId: null })}
                    />
                  )}
                </Form.Item>
                <Form.Item name="pipelineSubStageId" label={`${t("Pipeline Sub Stage")}`} rules={rules} required>
                  {showFieldSkeletons ? (
                    inputSkeleton()
                  ) : (
                    <SelectInput
                      placeholder={t("Select Sub Stage")}
                      options={subStages[stageId] ?? subStages?.[Number(stageId)]}
                      skeletonLoading={isStagesLoading}
                      inputLoading={isStagesLoading}
                    />
                  )}
                </Form.Item>
                <Form.Item
                  name="probabilityManual"
                  label={
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
                      {`${t("Probability")} (1% - 100%)`}
                      <Icon
                        icon="BsInfo"
                        size="16px"
                        color={theme["gray900"]}
                        hasBackground
                        iconContainerStyle={{ padding: 0 }}
                        tooltipText={t(
                          "Enter the likelihood (in percentage) that this deal will successfully close in the chosen stage during the selected month"
                        )}
                      />
                    </span>
                  }
                  rules={rules}
                  required
                >
                  {showFieldSkeletons ? (
                    inputSkeleton()
                  ) : (
                    <InputNumber min={1} max={100} style={{ width: "100%" }} placeholder={t("Enter probability")} />
                  )}
                </Form.Item>
                <Form.Item name="closingMonth" label={t("Closing Month")} rules={rules} required>
                  {showFieldSkeletons ? (
                    inputSkeleton()
                  ) : (
                    <MonthPicker
                      placeholder={t("Select month")}
                      format="MMM YYYY"
                      allowClear={false}
                      showTime={false}
                      containerWidth="100%"
                      value={form.getFieldValue("closingMonth")}
                      onChange={value => form.setFieldsValue({ closingMonth: value })}
                    />
                  )}
                </Form.Item>
              </Group>
              <Form.Item name="comment" label={t("Comment")} rules={rules} required>
                {showFieldSkeletons ? inputSkeleton() : <TextInput placeholder={t("Please add comment")} />}
              </Form.Item>
            </div>
          </Group>
        </Form>

        <ClientProfileForm
          formRef={clientProfileRef}
          client={clientPayload}
          allocatedTo={lead?.User?.name}
          onSuccess={() => {
            if (clientId) refetchClientDetail()
          }}
          onClose={() => {
            if (isClientProfileVisible) {
              resetInvestmentModalClientState()
              ref?.current?.setVisible(false)
              form.resetFields()
            }
          }}
        />
      </Modal>
    )
  }
)

AddInvestmentWealthPipelineModal.displayName = "AddInvestmentWealthPipelineModal"

export default AddInvestmentWealthPipelineModal
